import React, { Fragment, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconX, IconEdit, IconUpload, IconCircleCheck } from "@tabler/icons-react";
import { useAccountBot, useUpdateUserAccount, useTusUpload, useTusGet } from "hooks";
import { CommonUtils } from "utils";
import { LangConstant } from "const";

const BotAccountDialog = ({ open, onClose }) => {
  const { t: getLabel } = useTranslation();
  const fileInputRef = useRef(null);

  const [botAccountName, setBotAccountName] = useState("");
  const [description, setDescription] = useState("");
  const [isDisableUpdate, setIsDisableUpdate] = useState(false);
  const [botAvatarId, setBotAvatarId] = useState("");

  const { account, refetch, isFetching } = useAccountBot();
  const { updateUserAccountAsync } = useUpdateUserAccount();
  const { uploadAvatar } = useTusUpload();
  const { avatar } = useTusGet({ accountId: account.id, attachmentId: botAvatarId });

  const handleUpdateUser = () => {
    updateUserAccountAsync({
      accountId: account.id,
      name: botAccountName,
      description: description,
      avatar_id: botAvatarId,
      onClose: refetch,
    });
  };

  const onSuccessUpload = id => {
    setBotAvatarId(id);
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file) {
      const id = CommonUtils.uuid();
      uploadAvatar(id, account.id, file, onSuccessUpload);
    }
  };

  useEffect(() => {
    setBotAccountName(account.name);
    setDescription(account.description);
    setBotAvatarId(account.avatar_id);
  }, [account, open]);

  useEffect(() => {
    setIsDisableUpdate(
      !Boolean(botAccountName) ||
        (account.name === botAccountName &&
          JSON.parse(account.options || "{}").description === description &&
          account.avatar_id === botAvatarId) ||
        description?.length > 500,
    );
  }, [account, botAccountName, description, botAvatarId]);

  if (!open) return null;

  return (
    <Fragment>
      <div className="modal-backdrop fade show" />
      <div className="modal modal-blur fade show" style={{ display: "block" }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "400px" }}>
          <div className="modal-content">
            <div
              className="modal-header"
              style={{ backgroundColor: "var(--tblr-bg-surface-secondary)", height: "50px", display: "flex", alignItems: "center", padding: "8px" }}
            >
              <h5 style={{ fontWeight: 700, textAlign: "center", fontSize: 20, width: "95%", paddingLeft: "16px", margin: 0 }}>
                {getLabel(LangConstant.TXT_MEDIA_ACCOUNT_INFO)}
              </h5>
              <button className="btn btn-icon btn-ghost-secondary" onClick={onClose}>
                <IconX size={20} />
              </button>
            </div>

            <div className="modal-body" style={{ display: "flex", gap: "2px", flexDirection: "column", padding: "18px" }}>
              <div style={{ height: "150px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <div style={{ height: "120px", width: "120px", borderRadius: "50%", position: "relative", display: "flex", justifyContent: "center" }}>
                  {Boolean(avatar) ? (
                    <img
                      src={URL.createObjectURL(avatar)}
                      alt="avatar"
                      style={{ height: "120px", width: "120px", borderRadius: "50%", objectFit: "cover" }}
                    />
                  ) : (
                    <div style={{ height: "120px", width: "120px", backgroundColor: "#D9D9D9", borderRadius: "50%" }} />
                  )}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      width: "95px",
                      backgroundColor: "#D9D9D9",
                      borderBottomLeftRadius: "28em 18em",
                      borderBottomRightRadius: "28em 18em",
                      opacity: 0.2,
                      cursor: "pointer",
                    }}
                  >
                    <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
                    <span title={getLabel(LangConstant.TXT_MEDIA_UPLOAD_AVATAR)}>
                      <IconEdit
                        size={20}
                        style={{ marginLeft: "35px" }}
                        onClick={() => {
                          fileInputRef.current.click();
                        }}
                      />
                    </span>
                  </div>
                  <IconCircleCheck size={24} style={{ bottom: 0, position: "absolute", marginLeft: "80px", color: "#008FE8" }} />
                </div>
              </div>

              <div style={{ paddingTop: "20px" }}>
                <label className="form-label required">{getLabel(LangConstant.TXT_MEDIA_ACCOUNT_NAME)}</label>
                <input
                  className="form-control"
                  type="text"
                  value={botAccountName || ""}
                  onChange={e => {
                    setBotAccountName(e.target.value);
                  }}
                  autoComplete="off"
                  placeholder={getLabel(LangConstant.TXT_MEDIA_ACCOUNT_NAME_PLACEHOLDER)}
                />

                <div style={{ paddingTop: "8px" }}>
                  <strong style={{ paddingLeft: "8px" }}>{getLabel(LangConstant.TXT_MEDIA_DESCRIPTION)}</strong>
                  <textarea
                    className={`form-control ${description?.length > 500 ? "is-invalid" : ""}`}
                    rows="6"
                    placeholder={getLabel(LangConstant.TXT_MEDIA_INPUT_PLACEHOLDER)}
                    onChange={e => {
                      setDescription(e.target.value);
                    }}
                    value={description || ""}
                    style={{
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      fontSize: "1rem",
                      fontWeight: "400",
                      lineHeight: "1.5",
                    }}
                  />
                  {description?.length > 500 && (
                    <p style={{ paddingLeft: "8px", color: "red", margin: 0 }}>{getLabel(LangConstant.TXT_MEDIA_MAX_CHARS_ERROR)}</p>
                  )}
                </div>
              </div>

              <button
                className="btn btn-primary"
                style={{ marginTop: "28px" }}
                disabled={isDisableUpdate}
                onClick={handleUpdateUser}
              >
                {getLabel(LangConstant.L_UPDATE)}
              </button>
              <button
                className="btn"
                style={{ marginTop: "8px", backgroundColor: "var(--tblr-bg-surface-secondary)" }}
                onClick={onClose}
              >
                {getLabel(LangConstant.L_CANCEL)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default BotAccountDialog;
